// frontend/app/components/discoveryJourneys/JourneyLoopRow.js
//
// One link of the chain, and the bridge that hangs off it.
//
// `JourneyLoopRow` is a Learning Loop as the student meets it: its own question,
// how long it takes, where they are inside it, and one call to action. A loop
// whose lesson could not be resolved renders here as visibly unavailable with
// the server's reason and NO way to open it — and the row above and below it
// still draw, because a broken link must not break the chain.
//
// `JourneyBridge` is what makes the chain a story rather than a list: Saras
// asking the question that makes the next loop necessary.

import React from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import { tint } from "../lessonPlans/lessonPlanTheme";
import {
  fill,
  loopLessonMeta,
  loopStateOf,
  loopStepPosition,
  unavailableReason,
} from "./journeyProgress";

const RAIL_WIDTH = 34;

/**
 * Label, icon and which semantic colour a loop state is painted in. Resolved
 * against `colors` so every value has a dark-mode counterpart — an unavailable
 * loop is `warning`, not `danger`: it is an authoring gap for the teacher to
 * close, not something the student got wrong.
 */
export const loopStateMeta = (state, colors) => {
  switch (state) {
    case "complete":
      return { label: "Complete", icon: "check-circle", accent: colors.success };
    case "in_progress":
      return { label: "In progress", icon: "play-circle-outline", accent: colors.accentPrimary };
    case "unavailable":
      return { label: "Unavailable", icon: "alert-circle-outline", accent: colors.warning };
    default:
      return { label: "Not started", icon: "circle-outline", accent: colors.textMuted };
  }
};

const ctaFor = (state) =>
  state === "complete" ? "Review loop" : state === "in_progress" ? "Resume loop" : "Start loop";

export const JourneyLoopRow = ({ loop, index, isLast, colors, isDarkMode, onOpenLoop, t }) => {
  const state = loopStateOf(loop);
  const meta = loopStateMeta(state, colors);
  const isUnavailable = state === "unavailable";
  const { title: lessonTitle, durationMinutes, stepCount } = loopLessonMeta(loop);
  const position = loopStepPosition(loop);
  const reason = unavailableReason(loop);
  const number = Number(loop?.sequence) || index + 1;

  const heading = String(loop?.loop_title || lessonTitle || "").trim();
  const subtitle = String(loop?.loop_subtitle || "").trim();
  const cta = ctaFor(state);

  return (
    <View testID={`journey-loop-${number}`} style={styles.loopRow}>
      <View style={styles.rail}>
        <View
          style={[
            styles.railNode,
            {
              backgroundColor: state === "complete" ? meta.accent : colors.panelBg,
              borderColor: meta.accent,
            },
          ]}
        >
          <Text
            style={[
              styles.railNumber,
              { color: state === "complete" ? colors.panelBg : meta.accent },
            ]}
          >
            {number}
          </Text>
        </View>
        {isLast ? null : <View style={[styles.railLine, { backgroundColor: colors.border }]} />}
      </View>

      <View style={styles.loopBody}>
        <View
          style={[
            styles.loopCard,
            {
              backgroundColor: colors.panelBg,
              borderColor: isUnavailable ? colors.warning : colors.border,
              borderLeftColor: meta.accent,
            },
            isUnavailable ? styles.loopCardUnavailable : null,
          ]}
        >
          <View style={styles.loopHeader}>
            <Text style={[styles.loopEyebrow, { color: meta.accent }]}>
              {fill(t("Learning Loop {number}"), { number })}
            </Text>
            <View style={styles.loopState}>
              <MaterialCommunityIcons name={meta.icon} size={13} color={meta.accent} />
              <Text style={[styles.loopStateText, { color: meta.accent }]}>{t(meta.label)}</Text>
            </View>
          </View>

          {/* Each loop asks its own question — that is its title in the document. */}
          <Text style={[styles.loopTitle, { color: colors.textPrimary }]}>{heading}</Text>
          {subtitle ? (
            <Text style={[styles.loopSubtitle, { color: colors.textSecondary }]}>{subtitle}</Text>
          ) : null}

          {isUnavailable ? (
            <View
              testID={`journey-loop-unavailable-${number}`}
              style={[
                styles.reason,
                {
                  backgroundColor: tint(colors.warning, isDarkMode, 2),
                  borderColor: colors.teacherTipBorder,
                },
              ]}
            >
              <MaterialCommunityIcons name="alert-outline" size={14} color={colors.warning} />
              <Text style={[styles.reasonText, { color: colors.teacherTipText }]}>
                {reason || t("This loop's lesson could not be loaded.")}
              </Text>
            </View>
          ) : (
            <>
              <View style={styles.loopMeta}>
                {durationMinutes ? (
                  <View style={styles.metaItem}>
                    <MaterialCommunityIcons
                      name="clock-outline"
                      size={13}
                      color={colors.textMuted}
                    />
                    <Text style={[styles.metaText, { color: colors.textMuted }]}>
                      {fill(t("{minutes} min"), { minutes: durationMinutes })}
                    </Text>
                  </View>
                ) : null}
                {stepCount ? (
                  <View style={styles.metaItem}>
                    <MaterialCommunityIcons
                      name="format-list-numbered"
                      size={13}
                      color={colors.textMuted}
                    />
                    <Text style={[styles.metaText, { color: colors.textMuted }]}>
                      {fill(t(stepCount === 1 ? "{count} step" : "{count} steps"), {
                        count: stepCount,
                      })}
                    </Text>
                  </View>
                ) : null}
                {position && state !== "complete" ? (
                  <Text
                    testID={`journey-loop-position-${number}`}
                    style={[styles.metaText, { color: colors.textSecondary }]}
                  >
                    {fill(t("Step {step} of {total}"), position)}
                  </Text>
                ) : null}
              </View>

              <TouchableOpacity
                onPress={() => onOpenLoop(loop.lesson_plan_id, loop)}
                accessibilityRole="button"
                accessibilityLabel={`${t(cta)}: ${heading}`}
                style={[styles.loopCta, { borderTopColor: colors.border }]}
              >
                <Text style={[styles.loopCtaText, { color: meta.accent }]}>{t(cta)}</Text>
                <MaterialCommunityIcons name="arrow-right" size={15} color={meta.accent} />
              </TouchableOpacity>
            </>
          )}
        </View>
      </View>
    </View>
  );
};

/**
 * The connective tissue. Saras asks the question that makes the next loop
 * necessary — rendered whenever the author wrote one, including after the final
 * loop, because silently dropping authored content is the anti-goal this whole
 * design is written against.
 */
export const JourneyBridge = ({ prompt, nextLoop, index, colors, t }) => {
  const text = String(prompt || "").trim();
  if (!text) return null;
  const nextTitle = String(nextLoop?.loop_title || "").trim();

  return (
    <View testID={`journey-bridge-${index}`} style={styles.bridgeRow}>
      <View style={styles.rail}>
        <View style={[styles.railLine, { backgroundColor: colors.border }]} />
      </View>
      <View
        style={[styles.bridge, { backgroundColor: colors.aiCardBg, borderColor: colors.aiCardBorder }]}
      >
        <View style={styles.bridgeHeader}>
          <MaterialCommunityIcons
            name="comment-question-outline"
            size={14}
            color={colors.accentPrimary}
          />
          <Text style={[styles.bridgeLabel, { color: colors.accentPrimary }]}>
            {t("Saras asks")}
          </Text>
        </View>
        <Text style={[styles.bridgeText, { color: colors.textPrimary }]}>{text}</Text>
        {nextTitle ? (
          <Text style={[styles.bridgeNext, { color: colors.textMuted }]}>
            {fill(t("Next · {title}"), { title: nextTitle })}
          </Text>
        ) : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  loopRow: { flexDirection: "row", alignItems: "stretch" },
  loopBody: { flex: 1, minWidth: 0, paddingBottom: 4 },
  rail: { width: RAIL_WIDTH, alignItems: "center" },
  railNode: {
    width: 26,
    height: 26,
    borderRadius: 13,
    borderWidth: 2,
    alignItems: "center",
    justifyContent: "center",
    marginTop: 12,
  },
  railNumber: { fontSize: 12, fontWeight: "800" },
  railLine: { width: 2, flex: 1, minHeight: 12, marginTop: 4 },

  loopCard: {
    borderRadius: 14,
    borderWidth: 1,
    borderLeftWidth: 3,
    paddingTop: 12,
    marginBottom: 4,
  },
  // Held back from the chain's full weight so the eye reads it as a gap in the
  // story, without it disappearing — a teacher has to notice this.
  loopCardUnavailable: { opacity: 0.85 },
  loopHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 14,
    gap: 8,
  },
  loopEyebrow: {
    fontSize: 10,
    fontWeight: "800",
    letterSpacing: 0.8,
    textTransform: "uppercase",
  },
  loopState: { flexDirection: "row", alignItems: "center", gap: 4 },
  loopStateText: { fontSize: 11, fontWeight: "700", marginLeft: 4 },
  loopTitle: {
    fontSize: 16,
    fontWeight: "700",
    lineHeight: 21,
    paddingHorizontal: 14,
    marginTop: 4,
  },
  loopSubtitle: { fontSize: 13, lineHeight: 19, paddingHorizontal: 14, marginTop: 4 },
  loopMeta: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    paddingHorizontal: 14,
    paddingTop: 10,
    paddingBottom: 10,
    gap: 10,
  },
  metaItem: { flexDirection: "row", alignItems: "center", gap: 4 },
  metaText: { fontSize: 11, fontWeight: "600", marginLeft: 4 },

  reason: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 6,
    borderWidth: 1,
    borderRadius: 10,
    padding: 10,
    margin: 14,
    marginTop: 10,
  },
  reasonText: { flex: 1, fontSize: 12, lineHeight: 17, marginLeft: 6 },

  loopCta: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderTopWidth: 1,
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  loopCtaText: { fontSize: 12, fontWeight: "700" },

  bridgeRow: { flexDirection: "row", alignItems: "stretch" },
  bridge: {
    flex: 1,
    minWidth: 0,
    borderRadius: 12,
    borderWidth: 1,
    borderStyle: "dashed",
    padding: 12,
    marginVertical: 6,
  },
  bridgeHeader: { flexDirection: "row", alignItems: "center", gap: 5, marginBottom: 4 },
  bridgeLabel: {
    fontSize: 10,
    fontWeight: "800",
    letterSpacing: 0.8,
    textTransform: "uppercase",
    marginLeft: 5,
  },
  bridgeText: { fontSize: 14, fontWeight: "600", lineHeight: 20 },
  bridgeNext: { fontSize: 11, fontWeight: "700", marginTop: 6 },
});
